import { createContext, ErrorInfo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AvailabilityType, ContractedHoursType, IAvailability, IBookableresourceAvailability, IBookableresourcecategoryResponse, ICharacteristic, IContractedHours, IExpandCalendarAvailabity, IExpandCalendarResponse, IPayrollHourVariation, IPayrollHourVariationResponse, IResource, IResourceIngress, RepeatPattern } from "../interfaces/resource";
import { IProviderProps } from "../interfaces/provider";
import { apiContext } from "./api";
import { authContext } from "./auth";
import { EEventStatus } from "../helpers/calendar";
import { renderGenericFallbackUi } from "../components/error-boundary/generic-fallback-ui";
import { Icon } from "@fluentui/react";
import { addDays, addMinutes, addMonths, differenceInMinutes, endOfDay, format, isBefore, isSameDay, startOfDay, subDays, subMinutes, subMonths } from "date-fns";
import { IEvent, IEventBase } from "../interfaces/event";
import { availabilityFormatter, availabilityFortnightlyFormatter, contractedHoursFormatter, leaveFormatter } from "../helpers/shifts/shiftsFormatter";
import { ITravelCoordinate } from "../interfaces/travel";
import { boardContext } from "./boardContext";

// Track which CRM record type backs a resource.
type ResourceBacking = "systemuser" | "contact";
interface IRatingValueIngress {
    ratingvalueid: string;
    name: string;
    '_ratingmodel_value@OData.Community.Display.V1.FormattedValue'?: string;
}

interface IResourceContext {
    loading: string
    getResource: (id: string) => IResource | null
    isUserBackedResource: (id: string) => boolean
    getAllResources: () => IResource[]
    getResourceCount: () => number
    setResources: (resources: IResource[]) => void
    updateAvailability: (availability: IAvailability[]) => void
    getAvailabilities: () => IAvailability[]
    getAvailabilityByResource: (resourceId: string) => EEventStatus;
    getLeaves(): IEvent[];
    getIlluminaAvailablities: () => IEvent[];
}

const resourcesContext = createContext<IResourceContext>({
    loading: '',
    getAllResources: () => ([]),
    getResource: () => null,
    isUserBackedResource: () => false,
    getResourceCount: () => 0,
    setResources: (resources: IResource[]) => { },
    updateAvailability: (availability: IAvailability[]) => { },
    getAvailabilities: () => [],
    getAvailabilityByResource: (resourceId: string) => (EEventStatus.error),
    getLeaves: () => ([]),
    getIlluminaAvailablities: () => ([])
})

const ResourceProvider = (props: IProviderProps) => {
    const [resources, setResources] = useState<IResource[]>()
    const [availability, setAvailability] = useState<IAvailability[]>([])
    const [loading, setLoading] = useState<string>('Resources')
    const { get, getOrThrow, post } = useContext(apiContext)
    const { isAuthed } = useContext(authContext)
    const [loadError, setLoadError] = useState(false)
    const [leaveStore, setLeaveStore] = useState<IEvent[]>([]);
    const [illuminaAvailability, setIlluminaAvailablity] = useState<IEvent[]>([]);
    const { ready: boardReady, hasActiveSelection, siteIds, selectionKey } = useContext(boardContext);

    // Keep internal backing info separate from IResource so no internal DTO shape leaks to consumers.
    const resourceBackingByIdRef = useRef<Record<string, ResourceBacking>>({});

    const getBetweenFilter = (sd: Date, ed: Date, startColName: string, endColName: string): string => {
        //add/remove a day to include boundaries events
        var sdStr = format(sd, 'yyyy-MM-dd')
        var edStr = format(ed, 'yyyy-MM-dd')
        //https://forums.asp.net/t/1912609.aspx?Validate+overlapping+of+two+date+range
        let filter = `Microsoft.Dynamics.CRM.Between(PropertyName='${startColName}',PropertyValues=["${sdStr}","${edStr}"])`
        filter += ` or Microsoft.Dynamics.CRM.Between(PropertyName='${endColName}',PropertyValues=["${sdStr}","${edStr}"])`;
        filter += ` or (${startColName} le ${sdStr} and ${endColName} ge ${edStr})`;
        return `(${filter})`;
    }

    const mapPayrollHourVariation = (variation: IPayrollHourVariationResponse): IPayrollHourVariation | null => {
        if (!variation?._illumina_bookableresource_value) {
            return null;
        }

        const payrollStart = variation.illumina_payrollcycle?.illumina_startdate ? new Date(variation.illumina_payrollcycle.illumina_startdate) : null;
        const payrollEnd = variation.illumina_payrollcycle?.illumina_enddate ? new Date(variation.illumina_payrollcycle.illumina_enddate) : null;

        return {
            bookableResourceId: variation._illumina_bookableresource_value,
            payrollCycleId: variation._illumina_payrollcycle_value,
            maximumHoursPerPayroll: variation.illumina_maximumnoofworkhoursperpayroll,
            defaultHoursPerPayroll: variation.illumina_defaultnoofworkhoursperpayroll,
            minimumHoursPerPayroll: variation.illumina_minimumnoofworkhoursperpayroll,
            payrollStart: payrollStart ?? new Date(0),
            payrollEnd: payrollEnd ?? new Date(0)
        };
    };

    const fetchRatingValueMetadata = useCallback(async (ratingValueIds: string[]): Promise<Record<string, { name: string; ratingModelName?: string }>> => {
        const uniqueIds = Array.from(
            new Set(
                (ratingValueIds ?? [])
                    .map((id) => String(id ?? '').trim())
                    .filter((id) => id.length > 0)
            )
        );
        if (uniqueIds.length < 1) {
            return {};
        }

        const metadataById: Record<string, { name: string; ratingModelName?: string }> = {};
        const chunkSize = 20;

        for (let index = 0; index < uniqueIds.length; index += chunkSize) {
            const chunk = uniqueIds.slice(index, index + chunkSize);
            const response = await getOrThrow('/Entity/ratingvalues', {
                select: 'name,_ratingmodel_value',
                filter: chunk.map((id) => `ratingvalueid eq '${id}'`).join(' or ')
            }) as { value?: IRatingValueIngress[] };

            (response?.value ?? []).forEach((ratingValue) => {
                const id = String(ratingValue?.ratingvalueid ?? '').trim();
                if (!id) return;
                metadataById[id] = {
                    name: String(ratingValue?.name ?? '').trim(),
                    ratingModelName: String(ratingValue?.['_ratingmodel_value@OData.Community.Display.V1.FormattedValue'] ?? '').trim() || undefined
                };
            });
        }

        return metadataById;
    }, [getOrThrow]);

    useEffect(() => {
        resourceBackingByIdRef.current = {};
        setResources(undefined);
        setAvailability([]);
        setLeaveStore([]);
        setIlluminaAvailablity([]);
        setLoadError(false);
        setLoading(hasActiveSelection ? 'Resources' : '');
    }, [hasActiveSelection, selectionKey]);

    useEffect(() => {
        const loadResources = async (start: Date, end: Date) => {
            try {
                const newResources: IResource[] = []
                if (siteIds.length < 1) {
                    newResources.push({
                        title: "Unassigned",
                        id: "0",
                        index: 0,
                        characteristics: [],
                        categories: [],
                        approvedLeaves: []
                    });
                    setResources(newResources);
                    setLoading('');
                    return;
                }
                const siteAssociationFilter = siteIds.map((siteId) => `o/illumina_sitelocationid eq '${siteId}'`).join(' or ');
                const siteExpandFilter = siteIds.map((siteId) => `illumina_sitelocationid eq '${siteId}'`).join(' or ');

                //load leave requests for past and future six months
                let today = new Date();
                let leaveFilter = getBetweenFilter(subMonths(today, 6), addMonths(today, 6), 'msdyn_starttime', 'msdyn_endtime');
                let availabilityFilter = getBetweenFilter(subMonths(today, 6), addMonths(today, 6), 'illumina_startdate', 'illumina_enddate')
                const startDate = subDays(today, 14);
                const endDate = addDays(today, 14);
                const resourceCategories: { value: IBookableresourcecategoryResponse[] } = await get('/Entity/bookableresourcecategories', {
                    select: 'name,description,illumina_displaycolour',
                    filter: 'illumina_displaycolour ne null'
                });

                // Allow both Contact-backed (2) and User-backed (3) bookable resources.
                // In practice, a resource must still be explicitly associated to the current Site Location to appear.
                const resourceTypeFilter = `(resourcetype eq 2 or resourcetype eq 3)`;
                const parseCoordinate = (value: number | string | null | undefined): number | null => {
                    if (value == null || value === '') {
                        return null;
                    }
                    const parsed = Number(value);
                    return Number.isFinite(parsed) ? parsed : null;
                };

                const toResourceAddressCoordinates = (
                    ingress: IResourceIngress
                ): ITravelCoordinate => ({
                    latitude: parseCoordinate(ingress.UserId?.address1_latitude ?? ingress.ContactId?.address1_latitude),
                    longitude: parseCoordinate(ingress.UserId?.address1_longitude ?? ingress.ContactId?.address1_longitude)
                });

                const toResourceAddressLabel = (ingress: IResourceIngress): string | undefined => {
                    const line1 = String(ingress.UserId?.address1_line1 ?? ingress.ContactId?.address1_line1 ?? '').trim();
                    if (line1) {
                        return line1;
                    }

                    const composite = String(ingress.UserId?.address1_composite ?? ingress.ContactId?.address1_composite ?? '').trim();
                    return composite || undefined;
                };
                
                const resourceSelectFields = [
                    'name',
                    'illumina_resourcesummary',
                    'resourcetype',
                    'msdyn_hourlyrate',
                    'illumina_minimumworkhourperpayroll',
                    'illumina_defaultworkhourperpayroll',
                    'illumina_maximumworkhourperpayroll',
                    'illumina_maximumworkinghoursperday',
                    'illumina_maximumhourspershift',
                    'illumina_currentemploymenttype',
                    'illumina_applicableaward',
                    'illumina_passivesleepoverasbreak',
                    'illumina_minimumbreakbetweenshifts',
                    '_calendarid_value',
                    'msdyn_startlocation',
                    'msdyn_endlocation'
                ];
                const payrollVariationBaseSelectFields = [
                    '_illumina_payrollcycle_value',
                    '_illumina_bookableresource_value',
                    'illumina_maximumnoofworkhoursperpayroll',
                    'illumina_defaultnoofworkhoursperpayroll',
                    'illumina_minimumnoofworkhoursperpayroll'
                ];
                const commonExpandSegments = [
                    'UserId($select=firstname,lastname,photourl,internalemailaddress,mobilephone,address1_line1,address1_composite,address1_latitude,address1_longitude)',
                    'ContactId($select=firstname,lastname,emailaddress1,address1_line1,address1_composite,address1_latitude,address1_longitude)',
                    `illumina_availability_BookableResource_bookableresource($filter=${availabilityFilter} and statuscode eq 390950002)`,
                    'bookableresource_bookableresourcecharacteristic_Resource($select=name,bookableresourcecharacteristicid,_ratingvalue_value,illumina_expirydate,_characteristic_value,statecode;$filter=statecode eq 0)',
                    `illumina_BookableResource_illumina_sitelocation_il($select=illumina_name,illumina_sitelocationid,illumina_addressline1,illumina_addresscity,illumina_addressstateorprovince,illumina_addresspostalcode,illumina_latitudes,illumina_longitudes;$filter=${siteExpandFilter})`,
                    'bookableresource_bookableresourcecategoryassn_Resource($select=_resourcecategory_value)',
                    `msdyn_bookableresource_msdyn_timeoffrequest_Resource($filter=statecode eq 1 and _msdyn_approvedby_value ne null and (${leaveFilter}))`
                ];
                const resourceQueryVariants = [
                    {
                        label: 'full resource query',
                        select: resourceSelectFields.join(','),
                        payrollVariationFields: payrollVariationBaseSelectFields,
                        includePayrollVariations: true
                    },
                    {
                        label: 'without daily-hours field',
                        select: resourceSelectFields.filter((field) => field !== 'illumina_maximumworkinghoursperday').join(','),
                        payrollVariationFields: payrollVariationBaseSelectFields,
                        includePayrollVariations: true
                    },
                    {
                        label: 'without payroll default-hours variation field',
                        select: resourceSelectFields.filter((field) => field !== 'illumina_maximumworkinghoursperday').join(','),
                        payrollVariationFields: payrollVariationBaseSelectFields.filter((field) => field !== 'illumina_defaultnoofworkhoursperpayroll'),
                        includePayrollVariations: true
                    },
                    {
                        label: 'without payroll variation expand',
                        select: resourceSelectFields.filter((field) => field !== 'illumina_maximumworkinghoursperday').join(','),
                        payrollVariationFields: [],
                        includePayrollVariations: false
                    },
                    {
                        label: 'legacy resource query',
                        select: [
                            'name',
                            'illumina_resourcesummary',
                            'resourcetype',
                            'msdyn_hourlyrate',
                            'illumina_minimumworkhourperpayroll',
                            'illumina_maximumworkhourperpayroll',
                            'illumina_maximumhourspershift',
                            '_calendarid_value',
                            'msdyn_startlocation',
                            'msdyn_endlocation'
                        ].join(','),
                        payrollVariationFields: [],
                        includePayrollVariations: false
                    }
                ];

                let req: { value: IResourceIngress[] } | null = null;
                let lastResourceQueryError: unknown = null;
                for (const variant of resourceQueryVariants) {
                    try {
                        const expandSegments = [...commonExpandSegments];
                        if (variant.includePayrollVariations) {
                            expandSegments.push(
                                `illumina_payrollhourvariation_bookableres($select=${variant.payrollVariationFields.join(',')};$expand=illumina_payrollcycle($select=illumina_startdate,illumina_enddate))`
                            );
                        }
                        req = await getOrThrow('/Entity/bookableresources', {
                            select: variant.select,
                            orderby: 'lastname desc',
                            filter: `statecode eq 0 and ${resourceTypeFilter} and illumina_BookableResource_illumina_sitelocation_il/any(o:${siteAssociationFilter})`,
                            expand: expandSegments.join(',')
                        }) as { value: IResourceIngress[] };
                        if (variant.label !== 'full resource query') {
                            console.warn(`Roster resources loaded using fallback query (${variant.label}).`);
                        }
                        break;
                    } catch (error) {
                        lastResourceQueryError = error;
                        console.warn(`Roster resources query failed (${variant.label}).`, error);
                    }
                }
                if (!req) {
                    throw lastResourceQueryError ?? new Error('Unable to load roster resources.');
                }
                let ratingValueMetadata: Record<string, { name: string; ratingModelName?: string }> = {};
                try {
                    const ratingValueIds = req.value.flatMap((resource) =>
                        (resource.bookableresource_bookableresourcecharacteristic_Resource ?? [])
                            .map((characteristic) => String(characteristic['_ratingvalue_value'] ?? '').trim())
                            .filter((id) => id.length > 0)
                    );
                    ratingValueMetadata = await fetchRatingValueMetadata(ratingValueIds);
                } catch (error) {
                    console.warn('Unable to load rating value metadata for roster resources.', error);
                }

                let illuminaAvailabilities: IEvent[] = [];
                
                req.value.forEach((res) => {
                    const matchingLocations = (res.illumina_BookableResource_illumina_sitelocation_il ?? []).filter((location) =>
                        siteIds.includes(location.illumina_sitelocationid)
                    );
                    if (matchingLocations.length < 1) return;

                    // Record backing type internally (no DTO shape leaks through `IResource`).
                    // `resourcetype`: 2=Contact, 3=User.
                    resourceBackingByIdRef.current[res.bookableresourceid] = (res.resourcetype === 2 ? "contact" : "systemuser");

                    let resource = {
                        personalNote: res.illumina_resourcesummary,
                        title: res.name,
                        id: res.bookableresourceid,
                        profileImageUrl: res.UserId?.photoUrl,
                        primaryEmail: res.UserId?.internalemailaddress || res.ContactId?.emailaddress1,
                        primaryPhone: res.UserId?.mobilephone,
                        baseRateAUD: Number(res.msdyn_hourlyrate ?? 0) || undefined,
                        minimumWorkHoursPerPayroll: res.illumina_minimumworkhourperpayroll as number,
                        defaultWorkHoursPerPayroll: res.illumina_defaultworkhourperpayroll as number,
                        maximumWorkHourPerPayroll: res.illumina_maximumworkhourperpayroll as number,
                        maximumWorkingHoursPerDay: res.illumina_maximumworkinghoursperday as number,
                        currentEmploymentType: res.illumina_currentemploymenttype as number,
                        applicableAward: res.illumina_applicableaward as number,
                        minimumBreakBetweenShifts: res.illumina_minimumbreakbetweenshifts as number,
                        countPassiveSleepOverAsBreak: !!res.illumina_passivesleepoverasbreak,
                        maxHoursPerShift: res.illumina_maximumhourspershift,
                        calendarId: res._calendarid_value,
                        characteristics: res.bookableresource_bookableresourcecharacteristic_Resource
                            .map((ch): ICharacteristic => ({
                                ratingValueId: ch['_ratingvalue_value'],
                                name: ch.name,
                                id: ch.bookableresourcecharacteristicid,
                                ratingName: ch['_ratingvalue_value@OData.Community.Display.V1.FormattedValue'] ?? ratingValueMetadata[String(ch['_ratingvalue_value'] ?? '').trim()]?.name ?? '',
                                ratingModelName: ratingValueMetadata[String(ch['_ratingvalue_value'] ?? '').trim()]?.ratingModelName,
                                expiryDate: ch.illumina_expirydate,
                                characteristicId: ch['_characteristic_value']
                            })).sort((x, y) => x.name.localeCompare(y.name)),
                        siteLocation: matchingLocations,
                        startLocationType: res.msdyn_startlocation ?? null,
                        endLocationType: res.msdyn_endlocation ?? null,
                        startLocationLabel: res['msdyn_startlocation@OData.Community.Display.V1.FormattedValue'] ?? null,
                        endLocationLabel: res['msdyn_endlocation@OData.Community.Display.V1.FormattedValue'] ?? null,
                        resourceAddressCoordinates: toResourceAddressCoordinates(res),
                        resourceAddressLabel: toResourceAddressLabel(res),
                        index: newResources.length,
                        categories: [],
                        approvedLeaves: [],
                        availability: res.illumina_availability_BookableResource_bookableresource,
                        contractedHours: res.illumina_availability_BookableResource_bookableresource.filter(av => av.illumina_availabilitytype === AvailabilityType.ContractedHours && av.illumina_contractedhourstype === ContractedHoursType.Active),
                        payrollHourVariations: (res.illumina_payrollhourvariation_bookableres ?? [])
                            .map(mapPayrollHourVariation)
                            .filter((variation): variation is IPayrollHourVariation => !!variation)
                    } as IResource;

                    let availabilityContractedHours: IContractedHours[] = [];
                    const brAvailabilities: IEvent[] = [];
                    res.illumina_availability_BookableResource_bookableresource.forEach(y => {
                        if (y.illumina_availabilitytype === AvailabilityType.ContractedHours) {
                            availabilityContractedHours = availabilityContractedHours.concat(contractedHoursFormatter(y));
                        } else {
                            if (y.illumina_repeatpattern === RepeatPattern.Fortnightly) {
                                brAvailabilities.push(...availabilityFortnightlyFormatter(resource.id, y));
                            } else {
                                brAvailabilities.push(availabilityFormatter(resource.id, y));
                            }
                        }
                    });

                    if (availabilityContractedHours.length > 0) {
                        const contractedHoursEvents = groupAndCreateEvents(availabilityContractedHours, resource.id);
                        if (contractedHoursEvents.length > 0) {
                            illuminaAvailabilities = illuminaAvailabilities.concat(contractedHoursEvents);
                        }
                    }

                    

                    if (brAvailabilities.length > 0)
                        illuminaAvailabilities = illuminaAvailabilities.concat(brAvailabilities);
                    newResources.push(resource);
                    res.bookableresource_bookableresourcecategoryassn_Resource?.forEach(x => {
                        let rc = resourceCategories.value.find(y => y.bookableresourcecategoryid == x._resourcecategory_value);
                        if (rc != null)
                            resource.categories.push({
                                description: rc.description,
                                displayColour: rc.illumina_displaycolour,
                                id: rc.bookableresourcecategoryid,
                                name: rc.name
                            });
                    });
                    res.msdyn_bookableresource_msdyn_timeoffrequest_Resource.forEach(x => {
                        resource.approvedLeaves.push({ start: new Date(x.msdyn_starttime), end: new Date(x.msdyn_endtime), id: x.msdyn_timeoffrequestid });
                    })
                });
                setIlluminaAvailablity(illuminaAvailabilities);
                let leaves: IEvent[] = [];
                newResources.forEach(r => {
                    r.approvedLeaves.forEach(al => {
                        let e = leaveFormatter(al, r);
                        leaves.push(e);
                    })
                });
                setLeaveStore(leaves);

                // Ensure the special "Unassigned" resource is always the first row in the roster board.
                // Other resources remain sorted alphabetically by title.
                newResources.push({
                    title: "Unassigned",
                    id: "0",
                    // placeholder index; will be re-assigned below after sorting
                    index: -1,
                    characteristics: [],
                    categories: [],
                    approvedLeaves: []
                })

                // Keep "Unassigned" on top, then sort by name.
                newResources?.sort((a, b) => {
                    if (a.id === '0' && b.id !== '0') return -1;
                    if (a.id !== '0' && b.id === '0') return 1;
                    if (a.title > b.title) return 1;
                    if (a.title < b.title) return -1;
                    return 0
                }).forEach((resource, index) => {
                    resource.index = index;
                });



                setLoading('Calendars')

                // const expandCalendarRequest = {
                //     calendarIds: newResources.map(x => x.calendarId),
                //     start: start,
                //     end: end
                // }
                // const expandCalendarResponses: IExpandCalendarResponse[] = await post('/Rostering/expandcalendars', expandCalendarRequest);
                // expandCalendarResponses.forEach(r => {
                //     var br = newResources.find(nr => nr.calendarId == r.calendarId);
                //     if (br) {
                //         br.availabilities = r.availabilities.map((x: IExpandCalendarAvailabity) => {
                //             return { ...x, start: new Date(x.start), end: new Date(x.end) };
                //         });
                //     }
                // })

                setResources(newResources)
                setLoading('')
            } catch (e) {
                setLoadError(true)
            }

        }
        if ((!resources || resources.length < 1) && isAuthed && boardReady && hasActiveSelection) {
            let tempStart = subMonths(new Date(), 6);
            let tempEnd = addMonths(new Date(), 6);
            loadResources(tempStart, tempEnd)
        }
    }, [resources, isAuthed, boardReady, hasActiveSelection, siteIds, get, getOrThrow])

    const getResource = (id: string) => {
        if (!resources) {
            return null
        }
        return resources.find(res => res.id === id) ?? null
    }
    const isUserBackedResource = useCallback((id: string) => {
        const normalizedId = String(id ?? '').trim();
        if (!normalizedId || normalizedId === '0') return false;
        const backing = resourceBackingByIdRef.current[normalizedId];
        if (backing) return backing === "systemuser";
        return Boolean(resources?.some((resource) => resource.id === normalizedId));
    }, [resources]);
    const getResources = useCallback(() => {
        return resources ?? []
    }, [resources])

    const getLeaves = useCallback(() => {
        return leaveStore ?? []
    }, [resources])


    const getResourceCount = (): number => {
        return resources ? resources.length : 0
    }

    const overrideResources = (nResources: IResource[]) => {
        setResources(nResources)
    }

    const updateAvailability = (availability: IAvailability[]) => {
        setAvailability(availability)
    }

    const getAvailabilities = useCallback(() => {
        return availability ?? []
    }, [availability])

    const getAvailabilityByResource = (resourceId: string): EEventStatus => {
        const foundAvailability = availability.find(av => av.resourceId = resourceId)
        if (foundAvailability) {
            return foundAvailability.availability
        }
        return EEventStatus.valid

    }
    const getIlluminaAvailablities = useCallback((): IEvent[] => {
        return illuminaAvailability ?? [];
    }, [resources]);

    function groupAndCreateEvents(data: IContractedHours[], sourceId: string) {
        const groupedByDay = {};
        data.forEach(item => {
          const startDate = new Date(item.startDate);
          const endDate = new Date(item.endDate);
      
          const dayKey = format(startDate, 'yyyy-MM-dd'); 
          const eventId = `${item.id}_${dayKey}`; // Build a UI-only id, to ensure each day is unique and avoid ID collisions.
      
          // @ts-ignore
          if (!groupedByDay[dayKey]) {
            const start = startOfDay(startDate);
            const end = addMinutes(start, 30);
            // @ts-ignore
            groupedByDay[dayKey] = {
              start: start,
              end: end,
              id: eventId,
              editable: false,
              type: item.type,
              resourceEditable: false,
              events: [],
            };
          }
      
          // @ts-ignore
          groupedByDay[dayKey].events.push({
            ... item
          });
        });
      
        const events = Object.keys(groupedByDay).map(dayKey => {
        // @ts-ignore
          const dayData = groupedByDay[dayKey];
          let event = {
            resourceId: sourceId,
            title: `contractedHours`,
            id: dayData.id,
            display: 'contractedHours',
            description: '',
            editable: false,
            start: dayData.start,
            end: dayData.end,
            availabilityType: dayData.type,
            resourceEditable: false,
            extendedProps: {
                events: dayData.events 
            },
        }  as unknown as IEvent;
        return event;   
      });
      return events;
    }

    return (
        <resourcesContext.Provider value={{
            loading,
            getResource,
            isUserBackedResource,
            getResourceCount,
            getAllResources: getResources,
            setResources: overrideResources,
            getAvailabilities,
            updateAvailability,
            getAvailabilityByResource,
            getLeaves,
            getIlluminaAvailablities
        }}>
            {loadError ? (
                <div style={{
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: 'rgb(243, 112, 55)',
                    fontSize: '12px'
                }}>
                    <Icon iconName="error" style={{ fontSize: '24px' }} />
                    <p style={{ marginBottom: '0' }}>An error occurred whilst loading {loading.toLowerCase()}.</p>
                    <p>
                        Please <a
                            onClick={(e) => { e.preventDefault(); window.location.reload() }}
                            href={window.location.href}
                            style={{ color: 'rgb(243, 112, 55)', fontWeight: '500' }}
                        >
                            reload
                        </a> the page.
                    </p>
                </div>
            ) : (
                props.children
            )}
        </resourcesContext.Provider>
    )
}

export { ResourceProvider, resourcesContext }
